import React, { useState, useEffect } from 'react';
import Modal from '../../common/Modal';
import Button from '../../common/Button';
import ticketService from '../../../services/ticketService';
import { formatDateTime, formatTimeAgo } from '../../../utils/dateFormatter';
import { STATUS_COLORS, PRIORITY_COLORS, TICKET_TYPE_LABELS } from '../../../constants/statuses';
import { getSlaHoursByPriority, getSlaPlanLabel } from '../../../utils/slaConfig';
import api from '../../../services/api';

const ViewTicketModal = ({ isOpen, onClose, ticket, onEdit }) => {
  const [downloading, setDownloading] = useState(false);
  const [workReport, setWorkReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);

  useEffect(() => {
  if (isOpen && ticket?.id) {
    const fetchWorkReport = async () => {
      setLoadingReport(true);

      try {
        const response = await api.get(`/api/v1/work-reports/?ticket_id=${ticket.id}`);

        if (response.data && response.data.length > 0) {
          const sortedReports = response.data.sort((a, b) => b.id - a.id);
          setWorkReport(sortedReports[0]);
        } else {
          setWorkReport(null);
        }
      } catch (err) {
        console.error('Failed to fetch work report for ticket view', err);
        setWorkReport(null);
      } finally {
        setLoadingReport(false);
      }
    };

    fetchWorkReport();
  } else {
    setWorkReport(null);
  }}, [isOpen, ticket?.id]);

  if (!ticket) return null;

  const handleDownload = async (attachment) => {
    const result = await ticketService.downloadAttachment(ticket.id, attachment);
    if (!result.success) {
      alert(`Failed to download attachment: ${result.error}`);
    }
  };

  const handleDownloadReport = async () => {
    setDownloading(true);
    const result = await ticketService.downloadServiceReport(ticket.id);
    if (!result.success) {
      alert('Failed to download report: ' + result.error);
    }
    setDownloading(false);
  };

  const handlePreview = async (attachment) => {
    const result = await ticketService.previewAttachment(ticket.id, attachment);
    if (!result.success) {
      alert(`Failed to preview attachment: ${result.error}`);
    }
  };

  const getFileIcon = (mimeType) => {
    if (mimeType?.startsWith('image/')) return '';
    if (mimeType?.startsWith('video/')) return '';
    if (mimeType?.includes('pdf')) return '';
    if (mimeType?.includes('word')) return '';
    if (mimeType?.includes('excel') || mimeType?.includes('spreadsheet')) return '';
    return '';
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  };

  // Use real attachments from backend payload
  const allAttachments = ticket.attachments || [];

  // SLA status is derived from configured SLA hours in Admin Settings.
  const slaHours = getSlaHoursByPriority(ticket.priority);
  const createdAtDate = new Date(ticket.created_at);
  const closedAtDate = ticket.closed_at ? new Date(ticket.closed_at) : null;
  const slaDueDate = Number.isNaN(createdAtDate.getTime())
    ? null
    : new Date(createdAtDate.getTime() + slaHours * 60 * 60 * 1000);

  // Styles
  const sectionStyle = {
    marginBottom: '24px'
  };

  const sectionTitleStyle = {
    fontSize: '14px',
    fontWeight: '700',
    color: 'var(--text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '12px'
  };

  const infoGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px'
  };

  const infoItemStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  };

  const infoLabelStyle = {
    fontSize: '12px',
    color: 'var(--text-secondary)',
    fontWeight: '600'
  };

  const infoValueStyle = {
    fontSize: '14px',
    color: 'var(--text-primary)',
    fontWeight: '500'
  };

  const badgeStyle = (color) => ({
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
    background: `${color}20`,
    color: color,
    width: 'fit-content'
  });

  const descriptionBoxStyle = {
    padding: '16px',
    background: 'var(--surface-light)',
    borderRadius: '8px',
    fontSize: '14px',
    color: 'var(--text-primary)',
    lineHeight: '1.6',
    border: '1px solid var(--border)'
  };

  const timelineStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  };

  const timelineItemStyle = {
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-start'
  };

  const timelineDotStyle = {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    background: 'var(--primary)',
    marginTop: '4px',
    flexShrink: 0
  };

  const timelineContentStyle = {
    flex: 1
  };

  const timelineTitleStyle = {
    fontSize: '14px',
    fontWeight: '600',
    color: 'var(--text-primary)',
    marginBottom: '2px'
  };

  const timelineTimeStyle = {
    fontSize: '12px',
    color: 'var(--text-secondary)'
  };

  const attachmentListStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  };

  const attachmentItemStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    background: 'var(--surface-light)',
    borderRadius: '8px',
    border: '1px solid var(--border)'
  };

  const attachmentIconStyle = {
    fontSize: '32px',
    flexShrink: 0
  };

  const attachmentInfoStyle = {
    flex: 1,
    minWidth: 0
  };

  const attachmentNameStyle = {
    fontSize: '14px',
    fontWeight: '600',
    color: 'var(--text-primary)',
    marginBottom: '4px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  };

  const attachmentMetaStyle = {
    fontSize: '12px',
    color: 'var(--text-secondary)'
  };

  const attachmentActionsStyle = {
    display: 'flex',
    gap: '8px'
  };

  const footer = (
    <>
      <Button variant="secondary" onClick={onClose}>
        Close
      </Button>
      {ticket.status==="CLOSED" && <Button 
        variant="secondary" 
        onClick={handleDownloadReport}
        disabled={downloading}
      >
        {downloading ? ' Downloading...' : ' Download Report'}
      </Button>}
      {false && (
        <Button 
          variant="primary" 
          onClick={() => {
            onEdit(ticket);
            onClose();
          }}
        >
           Edit Ticket
        </Button>
      )}
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Ticket #${ticket.id}`}
      size="large"
      footer={footer}
    >
      <div>
        {/* Header Info */}
        <div style={sectionStyle}>
          <h3 style={{ fontSize: '20px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '12px' }}>
            {ticket.title}
          </h3>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <div style={badgeStyle(STATUS_COLORS[ticket.status])}>
              {ticket.status.replace('_', ' ')}
            </div>
            <div style={badgeStyle(PRIORITY_COLORS[ticket.priority])}>
              {ticket.priority}
            </div>
            <div style={badgeStyle('#6b7280')}>
              {TICKET_TYPE_LABELS[ticket.ticket_type] || ticket.ticket_type}
            </div>
          </div>
        </div>

        {/* Basic Information */}
        <div style={sectionStyle}>
          <div style={sectionTitleStyle}>Ticket Information</div>
          <div style={infoGridStyle}>
            <div style={infoItemStyle}>
              <div style={infoLabelStyle}>Ticket ID</div>
              <div style={infoValueStyle}>#{ticket.id}</div>
            </div>
            <div style={infoItemStyle}>
              <div style={infoLabelStyle}>System Type</div>
              <div style={infoValueStyle}>{ticket.system_type}</div>
            </div>
            <div style={infoItemStyle}>
              <div style={infoLabelStyle}>Problem Type</div>
              <div style={infoValueStyle}>
                {ticket.problem_type || <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>Not specified</span>}
              </div>
            </div>
            <div style={infoItemStyle}>
              <div style={infoLabelStyle}>Site</div>
              <div style={infoValueStyle}>{ticket.site_name}</div>
            </div>
            <div style={infoItemStyle}>
              <div style={infoLabelStyle}>Assigned To</div>
              <div style={infoValueStyle}>
                {ticket.assignee_name || <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>Unassigned</span>}
              </div>
            </div>
            {ticket.assignee_name && ticket.assigned_for && (
              <div style={infoItemStyle}>
                <div style={infoLabelStyle}>Assignment Time</div>
                <div style={infoValueStyle}>{formatDateTime(ticket.assigned_for)}</div>
              </div>
            )}
            <div style={infoItemStyle}>
              <div style={infoLabelStyle}>Creator ID</div>
              <div style={infoValueStyle}>{ticket.creator_email || ticket.source || '-'}</div>
            </div>
            {(ticket.status === 'CLOSED' || ticket.approver_name || ticket.approver?.name) && (
              <div style={infoItemStyle}>
                <div style={infoLabelStyle}>Approved By</div>
                <div style={infoValueStyle}>
                  {ticket.approver_name || ticket.approver?.name || <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>N/A</span>}
                </div>
              </div>
            )}
          </div>
        </div>

        {ticket.reopen_count > 0 && (
          <div style={sectionStyle}>
            <div style={sectionTitleStyle}>Reopen Details</div>
            <div style={infoGridStyle}>
              <div style={infoItemStyle}>
                <div style={infoLabelStyle}>Reopen Count</div>
                <div style={infoValueStyle}>{ticket.reopen_count}</div>
              </div>
              <div style={infoItemStyle}>
                <div style={infoLabelStyle}>Last Reopened At</div>
                <div style={infoValueStyle}>
                  {ticket.reopened_at ? formatDateTime(ticket.reopened_at) : '-'}
                </div>
              </div>
            </div>
            {ticket.reopen_reason && (
              <div style={{ ...descriptionBoxStyle, marginTop: '16px', borderLeft: '4px solid #f59e0b' }}>
                <strong style={{ display: 'block', marginBottom: '8px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                  LATEST REASON
                </strong>
                {ticket.reopen_reason}
              </div>
            )}
          </div>
        )}

        {/* Description */}
        {ticket.description && (
          <div style={sectionStyle}>
            <div style={sectionTitleStyle}>Description</div>
            <div style={descriptionBoxStyle}>
              {ticket.description}
            </div>
          </div>
        )}

        {/* Engineer Work Report Summary */}
        {(loadingReport || workReport) && (
          <div style={sectionStyle}>
            <div style={sectionTitleStyle}>Engineer's Work Report Summary</div>

            {loadingReport ? (
              <div style={{
                padding: '12px',
                background: 'var(--surface-light)',
                borderRadius: '8px',
                border: '1px solid var(--border)',
                fontSize: '13px',
                color: 'var(--text-secondary)',
                textAlign: 'center'
              }}>
                Loading engineer work report...
              </div>
            ) : workReport ? (
              <div style={{
                padding: '16px',
                background: 'var(--surface-light)',
                borderRadius: '8px',
                border: '1px solid var(--border)'
              }}>
                <div style={{ marginBottom: '12px' }}>
                  <span style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    color: 'var(--text-secondary)',
                    display: 'block'
                  }}>
                    Action Taken:
                  </span>
                  <p style={{
                    margin: '4px 0 0 0',
                    fontSize: '13px',
                    color: 'var(--text-primary)',
                    whiteSpace: 'pre-wrap',
                    lineHeight: '1.4'
                  }}>
                    {workReport.action_taken || 'N/A'}
                  </p>
                </div>

                {workReport.observations && (
                  <div>
                    <span style={{
                      fontSize: '12px',
                      fontWeight: '600',
                      color: 'var(--text-secondary)',
                      display: 'block'
                    }}>
                      Observations:
                    </span>
                    <p style={{
                      margin: '4px 0 0 0',
                      fontSize: '13px',
                      color: 'var(--text-primary)',
                      whiteSpace: 'pre-wrap',
                      lineHeight: '1.4'
                    }}>
                      {workReport.observations}
                    </p>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        )}

        {/* Dates */}
        <div style={sectionStyle}>
          <div style={sectionTitleStyle}>Timeline</div>
          <div style={infoGridStyle}>
            <div style={infoItemStyle}>
              <div style={infoLabelStyle}>Created</div>
              <div style={infoValueStyle}>{formatDateTime(ticket.created_at)}</div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                {formatTimeAgo(ticket.created_at)}
              </div>
            </div>
            <div style={infoItemStyle}>
              <div style={infoLabelStyle}>Due Date</div>
              <div style={infoValueStyle}>{formatDateTime(slaDueDate || ticket.due_date)}</div>
            </div>
            {workReport?.submitted_at && (
              <div style={infoItemStyle}>
                <div style={infoLabelStyle}>Report Submitted</div>
                <div style={infoValueStyle}>{formatDateTime(workReport.submitted_at)}</div>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                  {formatTimeAgo(workReport.submitted_at)}
                </div>
              </div>
            )}
            {ticket.closed_at && (
              <div style={infoItemStyle}>
                <div style={infoLabelStyle}>Closed</div>
                <div style={infoValueStyle}>{formatDateTime(ticket.closed_at)}</div>
              </div>
            )}
          </div>
        </div>

        {/* SLA Metrics */}
        {ticket.metrics && (
          <div style={sectionStyle}>
            <div style={sectionTitleStyle}>Metrics & SLA</div>
            <div style={infoGridStyle}>
              <div style={infoItemStyle}>
                <div style={infoLabelStyle}>SLA Plan</div>
                <div style={infoValueStyle}>{getSlaPlanLabel(ticket.priority)}</div>
              </div>
              <div style={infoItemStyle}>
                <div style={infoLabelStyle}>Attachments</div>
                <div style={infoValueStyle}>{ticket.metrics.attachment_count || 0}</div>
              </div>
            </div>
          </div>
        )}

        {/* Attachments Section */}
        <div style={sectionStyle}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: '12px' 
          }}>
            <div style={{ 
              fontSize: '14px',
              fontWeight: '700',
              color: 'var(--text-secondary)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
               Attachments ({allAttachments.length})
            </div>
          </div>

          {allAttachments.length > 0 ? (
            <div style={attachmentListStyle}>
              {allAttachments.map((attachment, index) => (
                <div key={attachment.id || index} style={attachmentItemStyle}>
                  <div style={attachmentIconStyle}>
                    {getFileIcon(attachment.mime_type)}
                  </div>
                  <div style={attachmentInfoStyle}>
                    <div style={attachmentNameStyle}>{attachment.file_name}</div>
                    <div style={attachmentMetaStyle}>
                      {formatFileSize(attachment.file_size_bytes)} • {formatTimeAgo(attachment.created_at)}
                    </div>
                  </div>
                  <div style={attachmentActionsStyle}>
                    <Button 
                      variant="ghost" 
                      size="small"
                      onClick={() => handlePreview(attachment)}
                    >
                       Preview
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="small"
                      onClick={() => handleDownload(attachment)}
                    >
                       Download
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{
              textAlign: 'center',
              padding: '40px 20px',
              background: 'var(--surface-light)',
              borderRadius: '8px',
              border: '1px dashed var(--border)'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '8px', opacity: 0.3 }}></div>
              <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                No attachments available for this ticket.
              </div>
            </div>
          )}
        </div>

        {/* Activity Timeline */}
        <div style={sectionStyle}>
          <div style={sectionTitleStyle}>Activity</div>
          <div style={timelineStyle}>
            <div style={timelineItemStyle}>
              <div style={timelineDotStyle}></div>
              <div style={timelineContentStyle}>
                <div style={timelineTitleStyle}>Ticket Created</div>
                <div style={timelineTimeStyle}>{formatDateTime(ticket.created_at)}</div>
              </div>
            </div>
            {ticket.assignee_name && (
              <div style={timelineItemStyle}>
                <div style={timelineDotStyle}></div>
                <div style={timelineContentStyle}>
                  <div style={timelineTitleStyle}>Assigned to {ticket.assignee_name}</div>
                  <div style={timelineTimeStyle}>{formatDateTime(ticket.assigned_for || ticket.created_at)}</div>
                </div>
              </div>
            )}
            {workReport?.submitted_at && (
              <div style={timelineItemStyle}>
                <div style={{ ...timelineDotStyle, background: 'var(--primary)' }}></div>
                <div style={timelineContentStyle}>
                  <div style={timelineTitleStyle}>Work Report Submitted</div>
                  <div style={timelineTimeStyle}>{formatDateTime(workReport.submitted_at)}</div>
                </div>
              </div>
            )}
            {ticket.status === 'CLOSED' && (
              <div style={timelineItemStyle}>
                <div style={{ ...timelineDotStyle, background: '#10b981' }}></div>
                <div style={timelineContentStyle}>
                  <div style={timelineTitleStyle}>Ticket Closed</div>
                  <div style={timelineTimeStyle}>{formatDateTime(ticket.closed_at)}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ViewTicketModal;